import { Component, DestroyRef, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { GridApi } from 'ag-grid-community';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { EmailDeliveryStep, EmailDeliveryTrackService, TAB_LABELS } from '../../tracking';
import { LoadableStatus } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { map } from 'rxjs';
import { EmailDeliveryTab } from '../../models/email-delivery-tab.enum';
import { EmailDeliveryHubPmsSyncService, EmailDeliveryHubSentRefreshService } from '../../services';
import { EmailDeliveryHubState, SetActiveTab } from '../../state';
import { ConfigService, PmSystem } from '@anduin/config';
import { LaunchDarklyFlag, LaunchDarklyService } from '@anduin/auth';
import { PendingEmailDatasource } from '../../components/email-delivery-hub-pending/services/pending-datasource.service';
import { FailedEmailDatasource } from '../../components/email-delivery-hub-failed/services/failed-datasource.service';
import { EmailDeliveryHubExportConfig } from '../../components/email-delivery-hub-sent-table';
import { ToolbarDensity } from './responsive-toolbar.directive';

@Component({
  selector: 'app-email-delivery-hub-container',
  templateUrl: './email-delivery-hub-container.component.html',
  styleUrls: ['./email-delivery-hub-container.component.scss'],
  standalone: false,
  providers: [EmailDeliveryHubPmsSyncService, EmailDeliveryHubSentRefreshService, EmailDeliveryTrackService],
  host: { class: 'h-100 p-4 d-flex flex-column gap-6 flex-1' },
})
export class EmailDeliveryHubContainerComponent implements OnInit, OnDestroy {
  protected readonly configService = inject(ConfigService);
  private readonly store = inject(Store);
  private readonly refreshService = inject(EmailDeliveryHubSentRefreshService);
  private readonly pmsSyncService = inject(EmailDeliveryHubPmsSyncService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pendingDatasource = inject(PendingEmailDatasource);
  private readonly failedDatasource = inject(FailedEmailDatasource);
  private readonly trackService = inject(EmailDeliveryTrackService);

  metricsData$ = this.store.select(EmailDeliveryHubState.getMetrics);

  LoadableStatus = LoadableStatus;

  areUnsentTabsEnabled = this.launchDarklyService.getFeatureFlagSignal(LaunchDarklyFlag.ENABLE_UNSENT_EMAIL_TABS);
  readonly EmailDeliveryTab = EmailDeliveryTab;

  // Initialized synchronously from the route snapshot so the correct tab is
  // selected immediately — no round-trip through the router required.
  // The subscription below keeps it in sync for browser back/forward navigation.
  readonly activeTab = signal<EmailDeliveryTab>((this.route.snapshot.params['tab'] as EmailDeliveryTab) ?? EmailDeliveryTab.SENT);

  readonly pendingCount = signal(0);
  readonly failedCount = signal(0);
  readonly activeGridApi = signal<GridApi | undefined>(undefined);
  readonly toolbarDensity = signal<ToolbarDensity>(0);
  readonly exportConfig = signal<EmailDeliveryHubExportConfig | undefined>(undefined);

  constructor() {
    this.store.dispatch(new SetActiveTab(this.activeTab()));

    this.route.params
      .pipe(
        map((p) => p['tab'] as EmailDeliveryTab),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((tab) => this.transitionToTab(tab));
  }

  onTabChange(tab: EmailDeliveryTab): void {
    this.transitionToTab(tab);
    this.router.navigate(['../', tab], { relativeTo: this.route });
  }

  // Route both router-driven and tab-click changes through one path so TAB_SWITCHED fires
  // with `fromTab` resolved from state BEFORE the state changes — and exactly once.
  private transitionToTab(tab: EmailDeliveryTab): void {
    const fromTab = this.activeTab();
    if (fromTab === tab) {
      return;
    }

    this.trackService.trackWorkflow(EmailDeliveryStep.TAB_SWITCHED, {
      fromTab: TAB_LABELS[fromTab],
      toTab: TAB_LABELS[tab],
    });

    // Drop the export config so the button can't act on the outgoing tab's grid; the incoming
    // tab's table re-emits a fresh one once its grid is ready.
    this.exportConfig.set(undefined);

    this.store.dispatch(new SetActiveTab(tab));
    this.activeTab.set(tab);
  }

  ngOnInit(): void {
    this.trackService.trackWorkflow(EmailDeliveryStep.VIEWED);

    const isSentTabActive = computed(() => this.activeTab() === EmailDeliveryTab.SENT);
    const isUnsentTabActive = computed(() => this.activeTab() !== EmailDeliveryTab.SENT);
    this.refreshService.init(isSentTabActive);
    this.pmsSyncService.init(isUnsentTabActive);

    this.pendingDatasource
      .getUndismissedCount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => this.pendingCount.set(count));

    this.failedDatasource
      .getUndismissedCount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => this.failedCount.set(count));
  }

  ngOnDestroy(): void {
    this.refreshService.destroy();
    this.pmsSyncService.destroy();
  }
  protected readonly PmSystem = PmSystem;
}
